import { useEffect, useState } from "react";
import {
    FaPlus,
    FaEdit,
    FaTrash,
    FaSearch,
    FaSpinner,
    FaArrowLeft
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { getMenuPermissions } from "../utils/auth";

import "./ManageRobots.css";

const API_URL = process.env.REACT_APP_API_URL;


function ManageRobots() {

    // Mirrors require_menu_permission("manage_robot") on the backend.
    const perms = getMenuPermissions("manage_robot");

    const emptyForm = {
        id: "",
        robot_sn: "",
        robot_name: "",
        robot_model: "",
        deployment_zone: "",
        reception_pos_name: "",
        is_active: true
    };

    const [robots, setRobots] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [search, setSearch] = useState("");

    const [showForm, setShowForm] = useState(false);
    const [isEdit, setIsEdit] = useState(false);

    const [form, setForm] = useState(emptyForm);

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        loadRobots();
    }, []);

    useEffect(() => {

        const value = search.toLowerCase();

        setFiltered(

            robots.filter(item =>
                item.robot_sn.toLowerCase().includes(value) ||
                (item.robot_name || "").toLowerCase().includes(value) ||
                (item.deployment_zone || "").toLowerCase().includes(value)
            )

        );

    }, [search, robots]);

    const loadRobots = async () => {

        setIsLoading(true);

        try {

            const res = await fetch(`${API_URL}/portal/robots`);

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const data = await res.json();

            if (Array.isArray(data)) {

                setRobots(data);

            } else if (data.status) {

                setRobots(data.data || []);

            } else {

                setRobots([]);

                toast.error(data.message || "Failed to load robots");

            }

        } catch (err) {

            console.error(err);

            setRobots([]);

            toast.error("Unable to load robots. Please try again.");

        } finally {

            setIsLoading(false);

        }

    };

    const handleChange = (e) => {

        const { name, value, type, checked } = e.target;

        setForm({
            ...form,
            [name]: type === "checkbox" ? checked : value
        });

    };

    const openAdd = () => {

        setIsEdit(false);

        setForm(emptyForm);

        setShowForm(true);

    };

    const openEdit = (item) => {

        setIsEdit(true);

        setForm({
            ...emptyForm,
            ...item
        });

        setShowForm(true);

    };

    const closeForm = () => {

        if (isSaving) return;

        setShowForm(false);

        setForm(emptyForm);

    };

    const saveRobot = async () => {

        if (!form.robot_sn) {

            toast.warn("Robot SN is required");
            return;

        }

        setIsSaving(true);

        try {

            const res = await fetch(`${API_URL}/portal/robots`, {

                method: isEdit ? "PUT" : "POST",

                headers: {
                    "Content-Type": "application/json"
                },

                body: JSON.stringify(form)

            });

            const data = await res.json();

            if (res.ok && data.status) {

                setShowForm(false);

                setForm(emptyForm);

                toast.success(
                    isEdit
                        ? "Robot updated successfully"
                        : "Robot added successfully - it will appear on the Robot Fleet page after the next sync"
                );

                loadRobots();

            } else {

                toast.error(data.message || "Failed to save robot");

            }

        } catch (err) {

            console.error("Failed to save robot:", err);

            toast.error("Something went wrong while saving the robot");

        } finally {

            setIsSaving(false);

        }

    };

    const deleteRobot = async (id) => {

        const ok = window.confirm(
            "Are you sure you want to remove this robot? Its fleet history will remain in the database, but it will stop being polled."
        );

        if (!ok) return;

        setDeletingId(id);

        try {

            const res = await fetch(`${API_URL}/portal/robots/${id}`, {

                method: "DELETE"

            });

            const data = await res.json();

            if (res.ok && data.status) {

                toast.success("Robot removed successfully");

                loadRobots();

            } else {

                toast.error(data.message || "Failed to remove robot");

            }

        } catch (err) {

            console.error("Failed to remove robot:", err);

            toast.error("Something went wrong while removing the robot");

        } finally {

            setDeletingId(null);

        }

    };

    return (

        <div className="page">

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
            />

            <div className="page-header">

                <div>
                    <h2>Manage Robot</h2>
                   {/*  <p className="page-subtitle">
                        Add a robot's Orion serial number here to have it polled by the Robot Fleet page -
                        no .env edit or restart needed.
                    </p> */}
                </div>

                {

                    !showForm && perms.add &&

                    <button
                        type="button"
                        className="add-btn"
                        onClick={openAdd}
                    >

                        <FaPlus />

                        Add Robot

                    </button>

                }

            </div>

            {

                showForm ? (

                    <div className="inline-form-container">

                        <div className="inline-form-header">

                            <button
                                type="button"
                                className="back-btn"
                                onClick={closeForm}
                                disabled={isSaving}
                            >

                                <FaArrowLeft />
                                Back to list

                            </button>

                            <h3>
                                {isEdit ? "Update Robot" : "Add Robot"}
                            </h3>

                        </div>

                        <div className="inline-form-grid">

                            <input
                                name="robot_sn"
                                placeholder="Robot SN (from OrionStar / the sticker on the unit)"
                                value={form.robot_sn}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="robot_name"
                                placeholder="Display Name (e.g. Robo1)"
                                value={form.robot_name || ""}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="robot_model"
                                placeholder="Robot Model (optional)"
                                value={form.robot_model || ""}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="deployment_zone"
                                placeholder="Deployment Zone (e.g. Terminal 1 - Check-In)"
                                value={form.deployment_zone || ""}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="reception_pos_name"
                                placeholder="Reception point name (must match a point on this robot's map)"
                                value={form.reception_pos_name || ""}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <label className="checkbox-field">
                                <input
                                    type="checkbox"
                                    name="is_active"
                                    checked={!!form.is_active}
                                    onChange={handleChange}
                                    disabled={isSaving}
                                />
                                Active (included in fleet polling)
                            </label>

                        </div>

                        <div className="inline-form-actions">

                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={closeForm}
                                disabled={isSaving}
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                className="save-btn"
                                onClick={saveRobot}
                                disabled={isSaving}
                            >

                                {
                                    isSaving
                                        ? <><FaSpinner className="spin" /> Saving...</>
                                        : isEdit
                                            ? "Update Robot"
                                            : "Save Robot"
                                }

                            </button>

                        </div>

                    </div>

                ) : (

                    <>

                        <div className="search-box">

                            <FaSearch />

                            <input
                                placeholder="Search by SN, name, deployment zone..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />

                        </div>

                        <div className="table-container">

                            <table>

                                <thead>

                                    <tr>
                                        <th>Robot SN</th>
                                        <th>Name</th>
                                        <th>Model</th>
                                        <th>Deployment Zone</th>
                                        <th>Reception Point</th>
                                        <th>Status</th>
                                        <th style={{ width: "120px" }}>Action</th>
                                    </tr>

                                </thead>

                                <tbody>

                                    {

                                        isLoading ?

                                            <tr>
                                                <td colSpan="7" style={{ textAlign: "center", padding: "40px" }}>
                                                    <FaSpinner className="spin" /> Loading robots...
                                                </td>
                                            </tr>

                                            :

                                            filtered.length === 0 ?

                                                <tr>
                                                    <td colSpan="7" style={{ textAlign: "center", padding: "40px" }}>
                                                        No robots registered yet - click "Add Robot" to get started.
                                                    </td>
                                                </tr>

                                                :

                                                filtered.map(item => (

                                                    <tr key={item.id}>

                                                        <td>{item.robot_sn}</td>

                                                        <td>{item.robot_name || "-"}</td>

                                                        <td>{item.robot_model || "-"}</td>

                                                        <td>{item.deployment_zone || "-"}</td>

                                                        <td>{item.reception_pos_name || "-"}</td>

                                                        <td>
                                                            <span className={`status-pill ${item.is_active ? "active" : "inactive"}`}>
                                                                {item.is_active ? "Active" : "Inactive"}
                                                            </span>
                                                        </td>

                                                        <td>

                                                            <div className="action-buttons">

                                                                {perms.edit &&
                                                                <button
                                                                    type="button"
                                                                    className="icon-btn edit-btn"
                                                                    title="Edit robot"
                                                                    aria-label="Edit robot"
                                                                    onClick={() => openEdit(item)}
                                                                    disabled={deletingId === item.id}
                                                                >
                                                                    <FaEdit />
                                                                </button>
                                                                }

                                                                {perms.delete &&
                                                                <button
                                                                    type="button"
                                                                    className="icon-btn delete-btn"
                                                                    title="Remove robot"
                                                                    aria-label="Remove robot"
                                                                    onClick={() => deleteRobot(item.id)}
                                                                    disabled={deletingId === item.id}
                                                                >
                                                                    {
                                                                        deletingId === item.id
                                                                            ? <FaSpinner className="spin" />
                                                                            : <FaTrash />
                                                                    }
                                                                </button>
                                                                }

                                                            </div>

                                                        </td>

                                                    </tr>

                                                ))

                                    }

                                </tbody>

                            </table>

                        </div>

                    </>

                )

            }

        </div>

    );

}

export default ManageRobots;
